import type { Order, OrderContractElement } from '@hero/schema';
import { useMemo } from 'react';
import {
  computeOrderDisplayInfo,
  type OrderDisplayForUser,
  type OrderDisplayPriceUi,
} from './computeOrderDisplayInfo';

export type { OrderDisplayForUser, OrderDisplayPriceUi } from './computeOrderDisplayInfo';
export { computeOrderDisplayInfo, orderAmountHasPrice } from './computeOrderDisplayInfo';

type UseOrderInfoConfig = {
  order: Order;
  /** Legacy layout hint — does not drive priceUi (see `viewerLeg`). */
  displayForUser: OrderDisplayForUser;
  isDriver?: boolean;
  /** Viewer's contract leg — canonical source for price display + edit. */
  viewerLeg?: OrderContractElement;
  /** Shipper of a walk-in order — enables client-first Add price. */
  isViewerShipper?: boolean;
};

export const useOrderDisplayInfo = (config: UseOrderInfoConfig) => {
  const { order, displayForUser, isDriver, viewerLeg, isViewerShipper } = config;

  return useMemo(
    () =>
      computeOrderDisplayInfo({
        order,
        displayForUser,
        isDriver,
        viewerLeg,
        isViewerShipper,
      }),
    [
      order,
      displayForUser,
      isDriver,
      viewerLeg,
      isViewerShipper,
      order.status?.order,
      order.contracts,
      viewerLeg?.contractId,
      viewerLeg?.amount,
      order.client,
      order.shipper,
    ],
  );
};

export type OrderDisplayInfo = ReturnType<typeof useOrderDisplayInfo>['orderInfo'];
export type OrderDisplayPricing = ReturnType<typeof useOrderDisplayInfo>['pricing'];
export type OrderDisplayStatus = ReturnType<typeof useOrderDisplayInfo>['status'];
export type OrderDisplayTimeline = ReturnType<typeof useOrderDisplayInfo>['timeline'];
export type OrderDisplayVisibility = ReturnType<typeof useOrderDisplayInfo>['visibility'];
export type OrderDisplayEditability = ReturnType<typeof useOrderDisplayInfo>['editability'];
